import { Chip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';

import { usePlanningLabels } from '../constants';
import { TaskRow } from '../types';
import type {
  HourlyRateModel,
  ProjectTaskStatus,
  TaskBillingType,
} from '@/api/REST/projects';
import { dataGridCellFontFamilyTabularNumsClassName } from '@/assets/themes/components/MuiDataGrid';
import { formatNumber } from '@/hooks/useFormatNumber';
import { useLocaleStore } from '@/stores/locale';
import { createDateColumn } from '@/utils/dataGrid';

interface UseColumnsOptions {
  currency?: string;
}

export const useColumns = ({
  currency,
}: UseColumnsOptions): GridColDef<TaskRow>[] => {
  const { lang } = useLocaleStore();
  const { t } = useTranslation('projectManagement');
  const { billingTypeLabels, taskStatusLabels, hourlyRateModelLabels } =
    usePlanningLabels();
  return [
    {
      field: 'taskNo',
      type: 'number',
      headerName: t('Columns.TaskNo'),
      width: 110,
      cellClassName: dataGridCellFontFamilyTabularNumsClassName,
    },
    {
      field: 'wbs',
      headerName: t('Columns.Wbs'),
      type: 'number',
      width: 110,
      cellClassName: dataGridCellFontFamilyTabularNumsClassName,
    },
    {
      field: 'startDate',
      headerName: t('Columns.Start'),
      ...createDateColumn(lang),
    },
    {
      field: 'endDate',
      headerName: t('Columns.End'),
      width: 130,
      ...createDateColumn(lang),
    },
    {
      field: 'hours',
      headerName: t('Columns.Hours'),
      description: t('Columns.ActualBudget'),
      headerAlign: 'right',
      align: 'right',
      width: 130,
      renderCell: ({ row }) => {
        const { actualHours, budgetHours } = row;
        return (
          <Typography
            component="span"
            color={actualHours > budgetHours ? 'error' : 'textPrimary'}
          >
            {actualHours ?? '-'}/{budgetHours ?? '-'}
          </Typography>
        );
      },
    },
    {
      field: 'amount',
      headerName: `${t('Columns.Amount')}${currency ? ` (${currency})` : ''}`,
      description: t('Columns.ActualBudget'),
      headerAlign: 'right',
      align: 'right',
      width: 200,
      renderCell: ({ row }) => {
        const { revenueAmount, budgetAmount } = row;
        const isExceeded = revenueAmount > budgetAmount;
        return (
          <Typography
            component="span"
            color={isExceeded ? 'error' : 'textPrimary'}
          >
            {formatNumber(revenueAmount, lang) ?? '-'}/
            {formatNumber(budgetAmount, lang) ?? '-'}
          </Typography>
        );
      },
    },
    {
      field: 'billingType',
      headerName: t('Columns.BillingType'),
      width: 160,
      valueFormatter: (value: TaskBillingType) =>
        billingTypeLabels[value] ?? value,
    },
    {
      field: 'hourlyRateModel',
      headerName: t('Columns.RateModel'),
      width: 130,
      valueFormatter: (value: HourlyRateModel) =>
        hourlyRateModelLabels[value] ?? value,
    },
    {
      field: 'status',
      headerName: t('Columns.Status'),
      width: 130,
      valueFormatter: (value: ProjectTaskStatus) =>
        taskStatusLabels[value] ?? value,
      renderCell: (params) => {
        const status = params.value as ProjectTaskStatus;
        const label = taskStatusLabels[status] ?? status;
        return (
          <Chip
            variant="outlined"
            color="secondary"
            size="small"
            label={label}
            monoText
          />
        );
      },
    },
  ];
};
